import { useEffect, useState } from 'react'
import { Send, Globe, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuthStore } from '@/store/authStore'
import api from '@/services/api'
import PageContainer from '@/components/common/PageContainer'
import PageHeader from '@/components/common/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'

export default function UpgradeRequest() {
  const { partner } = useAuthStore()
  const [countries, setCountries] = useState([])
  const [loadingCountries, setLoadingCountries] = useState(true)
  const [targetCountry, setTargetCountry] = useState('')
  const [motivation, setMotivation] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [pendingRequest, setPendingRequest] = useState(null)

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/countries/optimized')
        const list = res.data?.countries || []
        setCountries(
          list
            .filter((c) => c.supported)
            .map((c) => ({ value: c.code, label: c.name }))
            .sort((a, b) => a.label.localeCompare(b.label))
        )
      } finally {
        setLoadingCountries(false)
      }
    })()

    const requests = partner?.metadata?.upgradeRequests || []
    const pending = requests.find((r) => r.status === 'pending')
    if (pending) setPendingRequest(pending)
  }, [partner])

  async function submit() {
    if (!targetCountry) {
      toast.error('Selecciona el país que quieres gestionar')
      return
    }
    try {
      setIsSubmitting(true)
      await api.post('/partners/upgrade-request', { country: targetCountry, motivation })
      toast.success('Solicitud enviada. Admin revisa en 2-3 días hábiles.')
      setSubmitted(true)
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error al enviar solicitud')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (partner?.organizationType === 'country_manager') {
    return (
      <PageContainer>
        <Alert variant="info">
          <Globe size={16} />
          <AlertDescription>
            Ya eres Country Manager de <strong>{partner.managedCountry}</strong>. No puedes
            aplicar nuevamente.
          </AlertDescription>
        </Alert>
      </PageContainer>
    )
  }

  if (submitted || pendingRequest) {
    const req = pendingRequest || { targetCountry }
    return (
      <PageContainer>
        <Card>
          <CardContent className="space-y-3 pt-6">
            <div className="flex flex-wrap items-center gap-2">
              <Globe size={20} className="text-warning" />
              <h3 className="font-display text-h3 font-semibold text-foreground">
                Solicitud pendiente
              </h3>
              <Badge variant="warning">En revisión</Badge>
            </div>
            <p className="text-body-sm text-subtle">
              Tu solicitud para escalar a Country Manager de{' '}
              <strong>{req.targetCountry}</strong> está en revisión. Recibirás respuesta en
              2-3 días hábiles.
            </p>
            {req.motivation && (
              <div className="rounded-md bg-surface-muted p-3">
                <p className="text-caption text-subtle">Tu mensaje:</p>
                <p className="text-body-sm text-foreground mt-1">{req.motivation}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <PageHeader
        title="Aplicar como Country Manager"
        description="Eres partner activo. Solicita el upgrade para representar a CoreDesk Pro en un país."
      />

      <Alert variant="info">
        <AlertTitle>Qué cambia al ser Country Manager</AlertTitle>
        <AlertDescription>
          Acceso al Hub Corporativo, mini-CMS para anuncios y casos de éxito, equipo del Hub
          con permisos granulares, dashboard país y comisiones hasta 15% + bonos por meta.
        </AlertDescription>
      </Alert>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="space-y-1.5">
            <Label htmlFor="ur-country">País que quieres gestionar</Label>
            <Select
              value={targetCountry}
              onValueChange={setTargetCountry}
              disabled={loadingCountries}
            >
              <SelectTrigger id="ur-country">
                <SelectValue
                  placeholder={loadingCountries ? 'Cargando países…' : 'Selecciona'}
                />
              </SelectTrigger>
              <SelectContent>
                {countries.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ur-motivation">¿Por qué quieres ser Country Manager? (opcional)</Label>
            <Textarea
              id="ur-motivation"
              rows={5}
              value={motivation}
              onChange={(e) => setMotivation(e.target.value)}
              placeholder="Cuenta tu experiencia, visión para el país, red de contactos…"
            />
          </div>

          <Button onClick={submit} disabled={isSubmitting || !targetCountry} size="lg">
            {isSubmitting ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Enviando…
              </>
            ) : (
              <>
                <Send size={16} /> Enviar solicitud
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </PageContainer>
  )
}
